console.log('ProductForm.js: File loaded, script execution started.');

// Define PUB_SUB_EVENTS only if it's not already defined
if (typeof PUB_SUB_EVENTS === 'undefined') {
  window.PUB_SUB_EVENTS = {
    cartUpdate: 'cart:update',
    quantityUpdate: 'quantity:update',
    variantChange: 'variant:changed',
    cartError: 'cart:error',
  };
  console.log('ProductForm.js: PUB_SUB_EVENTS defined.');
} else {
  console.log('ProductForm.js: PUB_SUB_EVENTS already defined, using existing.');
}

try {
    class ProductForm extends HTMLElement {
        constructor() {
            super();
            console.log('ProductForm: Constructor invoked.');
            this.form = this.querySelector('form');
            this.submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;
            this.variantIdInput = this.form ? this.form.querySelector('input[name="id"]') : null;
            
            this.productId = this.dataset.productId;
            this.productData = null;
            this.currentVariant = null;

            if (!this.form) console.error('ProductForm Constructor: Form element not found within product-form.');
            if (!this.submitButton) console.error('ProductForm Constructor: Submit button not found within product-form.');
            if (!this.variantIdInput) console.error('ProductForm Constructor: Variant ID input not found.');
            if (!this.productId) console.warn('ProductForm Constructor: data-product-id attribute missing on <product-form> element.');
        }

        connectedCallback() {
            console.log('ProductForm: connectedCallback invoked.');
            if (!this.form) {
                console.error('ProductForm connectedCallback: Form not found, aborting initialization.');
                return;
            }

            this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
            this.addEventListener(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
            document.addEventListener(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
            
            this.fetchProductDataAndSetInitialState();
        }

        disconnectedCallback() {
            if (this.form) {
                this.form.removeEventListener('submit', this.onSubmitHandler.bind(this));
            }
            this.removeEventListener(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
            document.removeEventListener(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
        }

        onSubmitHandler(event) {
            console.log('ProductForm: onSubmitHandler triggered.');
            if (this.submitButton?.disabled) {
                console.log('ProductForm: Form submission prevented as button is disabled.');
                event.preventDefault();
                return false;
            }
        }

        handleVariantChange(event) {
            const variant = event.detail?.variant;
            if (!variant) return;

            console.log('ProductForm handleVariantChange: New variant:', variant);
            this.currentVariant = variant;
            if (this.variantIdInput) {
                this.variantIdInput.value = variant.id;
            }
            this.updateSubmitButtonState(variant);
        }
        
        fetchProductDataAndSetInitialState() {
            if (!this.productId) {
                console.warn('ProductForm: No product ID, relying on server-rendered state.');
                return;
            }

            const scriptElement = document.getElementById(`ProductJSON-${this.productId}`);
            if (!scriptElement) {
                console.warn('ProductForm: Product JSON script not found.');
                return;
            }

            try {
                this.productData = JSON.parse(scriptElement.textContent);
                const variants = this.productData.variants || (this.productData.product?.variants);
                
                if (!variants?.length) {
                    console.warn('ProductForm: No variants found in product data.');
                    return;
                }

                const currentVariantId = this.variantIdInput?.value;
                let selectedVariant = currentVariantId 
                    ? variants.find(v => v.id.toString() === currentVariantId)
                    : (variants.find(v => v.available) || variants[0]);

                if (selectedVariant) {
                    console.log('ProductForm: Setting initial variant:', selectedVariant);
                    this.currentVariant = selectedVariant;
                    if (this.variantIdInput) {
                        this.variantIdInput.value = selectedVariant.id;
                    }
                    this.updateSubmitButtonState(selectedVariant);
                }
            } catch (e) {
                console.error('ProductForm: Error parsing product JSON:', e);
            }
        }

        updateSubmitButtonState(variant) {
            if (!this.submitButton) {
                console.error('ProductForm: Submit button not found.');
                return;
            }

            if (!variant) {
                this.submitButton.textContent = 'Sin Stock';
                this.submitButton.disabled = true;
                this.submitButton.classList.add('button--sin-stock');
                this.submitButton.classList.remove('button--secondary', 'button--primary');
                this.variantIdInput?.setAttribute('disabled', '');
                console.log('ProductForm: No variant available');
                return;
            }

            console.log('ProductForm: Checking variant status:', {
                id: variant.id,
                inventory_management: variant.inventory_management,
                inventory_quantity: variant.inventory_quantity,
                inventory_policy: variant.inventory_policy,
                available: variant.available
            });

            // Enhanced stock checking logic
            const isShopifyManaged = variant.inventory_management === 'shopify';
            const hasNoStock = isShopifyManaged && variant.inventory_quantity <= 0;
            const denyWhenOutOfStock = variant.inventory_policy === 'deny';
            const isUnavailable = !variant.available;

            // A product is out of stock if:
            // 1. The variant is unavailable
            // 2. OR it's managed by Shopify AND has no stock AND denies purchases when out
            const isOutOfStock = isUnavailable || (hasNoStock && denyWhenOutOfStock);

            console.log('ProductForm: Stock status calculated:', {
                isShopifyManaged,
                hasNoStock,
                denyWhenOutOfStock,
                isUnavailable,
                isOutOfStock
            });

            if (isOutOfStock) {
                this.submitButton.textContent = 'Sin Stock';
                this.submitButton.disabled = true;
                this.submitButton.classList.add('button--sin-stock');
                this.submitButton.classList.remove('button--secondary', 'button--primary');
                this.variantIdInput?.setAttribute('disabled', '');
                console.log('ProductForm: Set to out of stock state');
            } else {
                const showDynamicCheckout = this.submitButton.classList.contains('button--secondary');
                this.submitButton.classList.remove('button--sin-stock');
                if (showDynamicCheckout) {
                    this.submitButton.classList.add('button--secondary');
                } else {
                    this.submitButton.classList.add('button--primary');
                }
                const allowsBackorder = isShopifyManaged && hasNoStock && !denyWhenOutOfStock;
                this.submitButton.textContent = allowsBackorder ? 'Pre-ordenar' : 'Agregar al carrito';
                this.submitButton.disabled = false;
                this.variantIdInput?.removeAttribute('disabled');
                console.log('ProductForm: Set to ' + (allowsBackorder ? 'pre-order' : 'in stock') + ' state');
            }
        }
    }

    if (!customElements.get('product-form')) {
        customElements.define('product-form', ProductForm);
        console.log('ProductForm.js: Custom element registered.');
    }

} catch (e) {
    console.error('ProductForm.js: Critical error:', e);
}

console.log('ProductForm.js: Script execution finished.');
